import type { ChipTone } from '@/design';
import type {
  RefreshDayOfWeek,
  RefreshFrequency,
  RefreshRunState,
  RefreshTrigger,
  RefreshWeekOfMonth
} from './api/brokercheck-refresh-contracts';
import { triggerLabel } from './work-items';

/**
 * The BrokerCheck refresh's words, and the one thing about it this side mirrors (SR-222, F10's R3,
 * R6, R7). The Schedule and Runs tabs and the queue's run filter draw a frequency, a day, a run's
 * state or a count through this one module.
 *
 * **A value with no word here is drawn as spelled**, never dropped and never mapped to a near
 * neighbour — the work items' rule (`work-items.ts`): survey-api adds vocabulary on its own
 * schedule, and the form keeps a stored code it cannot name rather than replacing it on Save.
 *
 * The option lists below (`FREQUENCIES`, `WEEKS_OF_MONTH`, `DAYS_OF_WEEK`) are the codes the form
 * offers, in survey-api's order — not a rule: survey-api judges every member of the `PUT`, and its
 * 400 is the answer to a code it does not take.
 *
 * **`REPORT_ORDER` is a mirror** of `BrokerCheckRefreshReport.cs` (survey-api's
 * `Application/BrokerCheckRefresh`), the `COLUMN_DEFAULTS` precedent, and moves with it: a run's
 * counts are a jsonb object whose member order means nothing, and the order the report mail an
 * admin received uses is the one the page draws, so the two agree. No count is summed or derived
 * here: the run keeps its counts in each number's own transaction, and the record is survey-api's.
 */

function labelOf(table: Record<string, string>, code: string): string {
  return table[code] ?? code;
}

const FREQUENCY_LABEL: Record<string, string> = { monthly: 'Monthly', weekly: 'Weekly' };

/** `BrokerCheckRefreshFrequencies.All`, the form's choices. */
export const FREQUENCIES: readonly RefreshFrequency[] = Object.keys(FREQUENCY_LABEL);

export function frequencyLabel(frequency: RefreshFrequency): string {
  return labelOf(FREQUENCY_LABEL, frequency);
}

const WEEK_LABEL: Record<string, string> = {
  first: 'First',
  second: 'Second',
  third: 'Third',
  fourth: 'Fourth',
  last: 'Last'
};

/** `BrokerCheckRefreshWeeks.All`, the form's choices for a monthly schedule. */
export const WEEKS_OF_MONTH: readonly RefreshWeekOfMonth[] = Object.keys(WEEK_LABEL);

export function weekOfMonthLabel(week: RefreshWeekOfMonth): string {
  return labelOf(WEEK_LABEL, week);
}

const DAY_LABEL: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
};

/** `BrokerCheckRefreshDays.All`, Monday first. */
export const DAYS_OF_WEEK: readonly RefreshDayOfWeek[] = Object.keys(DAY_LABEL);

export function dayOfWeekLabel(day: RefreshDayOfWeek): string {
  return labelOf(DAY_LABEL, day);
}

const TRIGGER_LABEL: Record<string, string> = { scheduled: 'Scheduled', manual: 'Run now' };

/** What started a run, in words: "Scheduled" for a slot's, "Run now" for an admin's. */
export function runTriggerLabel(trigger: RefreshTrigger): string {
  return labelOf(TRIGGER_LABEL, trigger);
}

const STATE_CHIP: Record<string, { label: string; tone: ChipTone }> = {
  running: { label: 'Running', tone: 'info' },
  paused: { label: 'Paused', tone: 'attention' },
  completed: { label: 'Completed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'neutral' }
};

/** A run's state as its chip draws it (R6). An unknown state is spelled and `neutral` — a tone is
 *  a claim about what the state means, the work items' rule. */
export function runStateChip(state: RefreshRunState): { label: string; tone: ChipTone } {
  return STATE_CHIP[state] ?? { label: state, tone: 'neutral' };
}

/** `BrokerCheckRefreshRunStates.Active`: the states Cancel is drawn on (R8), and the ones
 *  survey-api's index admits once. Whether a cancellation lands is still survey-api's to say. */
export function isRunActive(state: RefreshRunState): boolean {
  return state === 'running' || state === 'paused';
}

/** A run's count groups — the detail's `answers`, `failures` and `raised`, and since SR-396
 *  (its B7) IAPD's `iapdAnswers` and `iapdFailures`, counted apart for a person's or a member's
 *  number. */
export type RunCountGroup = 'answers' | 'failures' | 'iapdAnswers' | 'iapdFailures' | 'raised';

/** The groups in the report mail's order — `BrokerCheckRefreshReport.Render`: answers, failures,
 *  IAPD's answers, IAPD's failures, then the work items raised. */
export const RUN_COUNT_GROUPS: readonly RunCountGroup[] = [
  'answers',
  'failures',
  'iapdAnswers',
  'iapdFailures',
  'raised'
];

/**
 * The report's order, per group — `BrokerCheckRefreshReport.Render`: answers by `FactStatus`'s
 * order (`ok`, `not_found`, `error`) and then the rest by code; failures `not_found` first and then
 * by code; work items raised by code alone. IAPD's two groups are ordered as BrokerCheck's
 * (`ByStatus`, `ByReason`). "By code" is ordinal, as the C#'s `StringComparer.Ordinal` sorts.
 */
export const REPORT_ORDER: Record<RunCountGroup, readonly string[]> = {
  answers: ['ok', 'not_found', 'error'],
  failures: ['not_found'],
  iapdAnswers: ['ok', 'not_found', 'error'],
  iapdFailures: ['not_found'],
  raised: []
};

function ordinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** One group's counts in the report's order, each key and its count as sent. */
export function orderedCounts(
  group: RunCountGroup,
  counts: Record<string, number>
): [string, number][] {
  const order = REPORT_ORDER[group];
  const rank = (key: string) => {
    const at = order.indexOf(key);
    return at >= 0 ? at : order.length;
  };
  return Object.entries(counts).sort(([a], [b]) => rank(a) - rank(b) || ordinal(a, b));
}

const COUNT_LABEL: Record<Exclude<RunCountGroup, 'raised'>, Record<string, string>> = {
  answers: { ok: 'Found', not_found: 'Not on BrokerCheck', error: 'Lookup failed' },
  failures: { not_found: 'Not on BrokerCheck' },
  iapdAnswers: { ok: 'Found', not_found: 'Not on IAPD', error: 'Lookup failed' },
  iapdFailures: { not_found: 'Not on IAPD' }
};

/** A count's key in words — an answer status, a failure reason, a work item's trigger — or
 *  `null` where this build has none, for the key to be drawn as spelled. */
export function countLabel(group: RunCountGroup, key: string): Nullable<string> {
  if (group === 'raised') {
    const label = triggerLabel(key);
    return label === key ? null : label;
  }
  return COUNT_LABEL[group][key] ?? null;
}

/**
 * The refresh's clock: every instant on its screens drawn in **the schedule's own time zone**
 * (F10's Q2, R11) — what the schedule is written in and what survey-api's report mail uses, so the
 * page, the form's start and the mail say the same hour. Rejected: the viewer's zone, as every
 * other admin screen draws an instant — a slot that starts "Sat 00:00" in New York would read
 * "Fri 23:00" in Chicago beside a form saying 00:00.
 *
 * `zone` is the id the browser resolved the schedule's to, named once per screen. A zone the
 * browser cannot resolve — survey-api's runtime and the browser's need not agree on the list — is
 * drawn in UTC, and `fallback` says so: the page names the zone it used, never one it did not.
 * This is display alone; survey-api's slot arithmetic is never redone here.
 */
export interface IRefreshClock {
  zone: string;
  /** The schedule's zone could not be resolved here, so `zone` is UTC. */
  fallback: boolean;
  /** "Sat 7 Nov 2026, 00:00"; an instant that does not parse is drawn as sent. */
  instant(iso: string): string;
  /** A slot's window — "Sat 7 Nov 2026, 00:00 – Mon 9 Nov, 06:00", the end's year said only
   *  where it differs. */
  window(startsAt: string, endsAt: string): string;
}

export function refreshClock(timeZone: string): IRefreshClock {
  let zone = 'UTC';
  let fallback = true;
  try {
    zone = new Intl.DateTimeFormat('en-GB', { timeZone }).resolvedOptions().timeZone;
    fallback = false;
  } catch {
    // A RangeError: an id this browser does not know. Drawn in UTC, and said.
  }
  const format = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });
  // Assembled from parts: the locale puts a comma after the weekday only when a year follows,
  // and a window's two ends would read differently.
  const parts = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    const part = Object.fromEntries(format.formatToParts(date).map((p) => [p.type, p.value]));
    return {
      day: `${part.weekday} ${part.day} ${part.month}`,
      year: part.year,
      time: `${part.hour}:${part.minute}`
    };
  };
  const instant = (iso: string) => {
    const at = parts(iso);
    return at === null ? iso : `${at.day} ${at.year}, ${at.time}`;
  };
  return {
    zone,
    fallback,
    instant,
    window(startsAt, endsAt) {
      const start = parts(startsAt);
      const end = parts(endsAt);
      if (start === null || end === null) return `${instant(startsAt)} – ${instant(endsAt)}`;
      const endDay = end.year === start.year ? end.day : `${end.day} ${end.year}`;
      return `${start.day} ${start.year}, ${start.time} – ${endDay}, ${end.time}`;
    }
  };
}
